'use client'

import MD from '@/components/markdown'
import Reveal from '@/components/reveal'
import { Button } from '@/components/ui/button'
import { SITE_IS_JBDA } from '@/config/site.config'
import { useConsent } from '@/contexts/ConsentContext'
import getAPI from '@/lib/utils/api-client'
import { cn } from '@/lib/utils/utils'
import { useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import validator from 'validator'
import { getI18n, I18nDictionary, LangCode } from '../../../i18n/lang-context'
import { FlexCol } from '../../../ui/flex'
import { useCookiesConsentDialog } from '../../cookies-dialog'
import { JBDAPromiseBG } from '../../jbda/jbda-promise-bg'
import { Section } from '../../section'
import { SectionTitle } from '../../section-title'
import { LBPromiseBG } from '../lb-promise-bg'

const I18N: I18nDictionary = {
  title_JBDA: {
    fr: '*Contactez* notre équipe *JBDA*',
    en: '*Contact* our Team from *JBDA*'
  },
  title_LB: {
    fr: '*Contactez nous* à propos de *Legibloq*',
    en: '*Contact us* about *Legibloq*'
  },
  desc_LB: {
    en: 'For any inquiries or support related to Legibloq, please select the reason for your contact from the dropdown menu and enter your message in the field below:',
    fr: 'Pour toutes questions ou assistance liée à Legibloq, veuillez sélectionner la raison de votre contact dans le menu déroulant et saisir votre message dans le champ ci-dessous :'
  },
  select_reason_LB: {
    en: 'Please select the reason for your contact:',
    fr: 'Veuillez sélectionner la raison de votre contact :'
  },
  reasons_LB: {
    real_estate: {
      en: 'Request for information about real estate acquisition',
      fr: 'Demande d\'information sur l\'achat immobilier'
    },
    tax_inquiry: {
      en: 'Request for connection with a partner tax lawyer',
      fr: 'Demande de mise en relation avec un avocat fiscaliste partenaire'
    },
    partnership_inquiry: {
      en: 'Partnership Inquiry',
      fr: 'Demande de partenariat'
    },
    general_question: {
      en: 'General Question',
      fr: 'Question générale'
    },
    unsubscribe: {
      en: 'Unsubscribe from newsletter',
      fr: 'Désinscription de la newsletter'
    },
    subscribe: {
      en: 'Subscribe to newsletter',
      fr: 'Inscription à la newsletter'
    },
    support: {
      en: 'Support',
      fr: 'Support'
    },
    other: {
      en: 'Other',
      fr: 'Autre'
    }
  },
  label_message_LB: {
    en: 'Your Message:',
    fr: 'Votre message :'
  },
  placeholder_message_LB: {
    en: 'Type your message here...',
    fr: 'Tapez votre message ici...'
  },
  label_first_name_LB: {
    en: 'Your First Name:',
    fr: 'Votre prénom :'
  },
  placeholder_first_name_LB: {
    en: 'Type your first name here...',
    fr: 'Tapez votre prénom ici...'
  },
  label_last_name_LB: {
    en: 'Your Last Name:',
    fr: 'Votre nom :'
  },
  placeholder_last_name_LB: {
    en: 'Type your last name here...',
    fr: 'Tapez votre nom ici...'
  },
  label_email_LB: {
    en: 'Your Email address:',
    fr: 'Votre adresse email :'
  },
  placeholder_email_LB: {
    en: 'Type your email here...',
    fr: 'Tapez votre adresse email ici...'
  },
  action_LB: {
    en: 'Send Message',
    fr: 'Envoyer le Message'
  },
  sendingMessage: {
    en: 'Sending message...',
    fr: 'Envoi du message...'
  },
  sendSuccess: {
    en: 'Message sent successfully',
    fr: 'Message envoyé avec succès'
  },
  sent: {
    en: 'Message sent successfully. We\'ll be in touch soon!',
    fr: 'Message envoyé avec succès. Nous vous contacterons bientôt !'
  },
  newMessage: {
    en: 'New message',
    fr: 'Nouveau message'
  },
  emailInvalid: {
    en: 'Invalid email address !',
    fr: 'Adresse e-mail invalide !'
  },
  unknownError: {
    en: 'Oops ! An unknown error occured...',
    fr: 'Oups ! Une erreur inconnue s\'est produite...'
  },
  successfulSubscription: {
    en: 'Successful Subscription!',
    fr: 'Inscription réussie !',
  },
  successfulUnsubscription: {
    en: 'Successful Unsubscription.',
    fr: 'Désinscription réussie.',
  }
}

export function ContactUsForm({_reason, _email, _firstName, _lastName, langCode}: {_reason?: string, _email?: string, _firstName?: string, _lastName?: string, langCode: LangCode}) {

  const i18n = getI18n(I18N, langCode)
  const api = getAPI()
  const [reasonKey, setReasonKey] = useState<string>(i18n.reasons_LB[0])
  const [message, setMessage] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const { consent } = useConsent()

  const selectReasonRef = useRef<HTMLSelectElement>(null)
  const searchParams = useSearchParams()

  useEffect(() => {
    const email = _email || searchParams.get('email') || undefined
    const firstName = _firstName || searchParams.get('firstName') || undefined
    const lastName = _lastName || searchParams.get('lastName') || undefined
    const reasonKey = _reason || searchParams.get('reason') || undefined
    const reasonDesc = reasonKey && i18n.reasons_LB[reasonKey]
    resetForm()
    if(email) {
      setEmail(email)
    }
    if(firstName) {
      setFirstName(firstName)
    }
    if(lastName) {
      setLastName(lastName)
    }
    if(reasonDesc){
      setReasonKey(reasonKey)
    }
    if (selectReasonRef.current) {
      selectReasonRef.current.focus()
    }
  }, [])

  const isFirstNameValid = () => {
    if (!firstName || firstName.trim() === '' ) return false
    return true
  }
  const isLastNameValid = () => {
    if (!lastName || lastName.trim() === '' ) return false
    return true
  }

  const isEmailValid = () => {
    if (!email || email === '') return false
    if (!validator.isEmail(email)) return false
    return true
  }

  const isMessageValid = () => {
    if (!message || message === '') return false
    return true
  }

  const isFormValid = () => {
    if (!isFirstNameValid()) return false
    if (!isLastNameValid()) return false
    if (!isEmailValid()) return false
    if (!isUnsubscribe() && !isSubscribe() && !isMessageValid()) return false
    return true
  }
  const isUnsubscribe = () => {
    return reasonKey === 'unsubscribe'
  }
  const isSubscribe = () => {
    return reasonKey === 'subscribe'
  }

  const _doSendMessage = async () => {
    if (!isFormValid()) return
    try {
      setErrorMsg('')
      setSending(true)
      setSent(false)
      const reasonDesc = i18n.reasons_LB[reasonKey]
      console.log('Sending message [' + reasonDesc + '] from ' + email + ' (' + firstName + '-' + lastName + '):', message)
      const response = isUnsubscribe() || isSubscribe() ?
        await api.post('/email/newsletter/'+ (isUnsubscribe() ? 'unsubscribe' : 'subscribe'), { firstName, lastName, email, langCode }) :
        await api.post('/email/contact', { firstName, lastName, email, reason: reasonDesc, message: message })
      console.log('POST /email/contact RESPONSE', response)
      setSent(true)
      toast.success(i18n.sendSuccess, { autoClose: 5000 })
    } catch (e: any) {
      setErrorMsg(i18n.unknownError)
      toast.error(i18n.unknownError, { autoClose: 5000 })
    }
    setSending(false)
  }
  
  const { openCookiesConsentDialog } = useCookiesConsentDialog(langCode, _doSendMessage)

  const sendMessage = async () => {
    if (consent.contact) {
      _doSendMessage()
    } else {
      openCookiesConsentDialog('simple', langCode)
    }
  }

  const resetForm = () => {
    setReasonKey(Object.keys(i18n.reasons_LB)[0])
    setMessage('')
    setFirstName('')
    setLastName('')
    setEmail('')
    setErrorMsg('')
    setSending(false)
    setSent(false)
  }

  return (

    <div id='CONTACT_US_SECTION' className='relative w-full overflow-hidden'>

      {SITE_IS_JBDA ? <JBDAPromiseBG /> : <LBPromiseBG />}

      <div id='SECTION_CONTENT' className='relative z-10 m-auto w-full max-w-7xl p-4 py-16 sm:py-16'>

        <FlexCol className='w-full items-center gap-8 md:pt-32'>
          {SITE_IS_JBDA ?
            <SectionTitle title={i18n.title_JBDA} langCode={langCode} titleMaxWidth='max-w-2xl' titleText='text-4xl md:text-5xl' mainTitle={true} /> :
            <>
              <SectionTitle title={i18n.title_LB} langCode={langCode} titleMaxWidth='max-w-2xl' titleText='text-4xl md:text-5xl' mainTitle={true} />
              {sent ?
                <Section contentClassName='text-xl'>
                  {isUnsubscribe() ? i18n.successfulUnsubscription : isSubscribe() ? i18n.successfulSubscription : i18n.sent}
                  <Button className='mt-4' onClick={() => resetForm()}>Send another message</Button>
                </Section> :
                <Section containerClassName='relative overflow-hidden max-w-3xl gap-4' contentClassName='p-0 sm:p-4'>
                  <form id='CONTACT_FORM' className='relative z-10 w-full' onSubmit={e => { e.preventDefault(); sendMessage() }}>
                    <FlexCol className='mb-4 w-full gap-4'>
                      <div className='w-full text-center text-base'><MD langCode={langCode}>{i18n.desc_LB}</MD></div>
                      <select ref={selectReasonRef} className='w-full rounded-lg border border-gray-300 p-4 text-base' aria-label={i18n.select_reason_LB}
                        onChange={e => {setReasonKey(e.target.value)}}
                        value={reasonKey}>
                        {Object.keys(i18n.reasons_LB).map((reasonKey: string, index: number) => (
                          <option key={index} value={reasonKey}>{i18n.reasons_LB[reasonKey]}</option>
                        ))}
                      </select>
                    </FlexCol>
                    {!isUnsubscribe() && !isSubscribe() && <FlexCol className='mb-4 w-full gap-4'>
                      <label htmlFor='messageInput' className='w-full text-center text-base'><MD langCode={langCode}>{i18n.label_message_LB}</MD></label>
                      <textarea
                        id='messageInput'
                        className={cn('w-full rounded-lg border p-4 text-base', isMessageValid() ? 'border-gray-300' : 'border-red-600/40')}
                        placeholder={i18n.placeholder_message_LB}
                        rows={5}
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                      ></textarea>
                    </FlexCol>}
                    <div className='mb-4 w-full flex flex-col md:flex-row flex-wrap justify-start items-start gap-10'>
                      <FlexCol className='w-[47%]'>
                        <label htmlFor='nameInput' className='w-full text-center text-base'><MD langCode={langCode}>{i18n.label_first_name_LB}</MD></label>
                        <input
                          id='firstNameInput'
                          className={cn('w-full rounded-lg border p-4 text-base', isFirstNameValid() ? 'border-gray-300' : 'border-red-600/40')}
                          placeholder={i18n.placeholder_first_name_LB}
                          value={firstName}
                          onChange={e => setFirstName(e.target.value)}
                        ></input>
                      </FlexCol>
                      <FlexCol className='w-[47%]'>
                        <label htmlFor='nameInput' className='w-full text-center text-base'><MD langCode={langCode}>{i18n.label_last_name_LB}</MD></label>
                        <input
                          id='nameInput'
                          className={cn('w-full rounded-lg border p-4 text-base', isLastNameValid() ? 'border-gray-300' : 'border-red-600/40')}
                          placeholder={i18n.placeholder_last_name_LB}
                          value={lastName}
                          onChange={e => setLastName(e.target.value)}
                        ></input>
                      </FlexCol>
                    </div>
                    <FlexCol className='mb-4 w-full gap-4'>
                      <label htmlFor='emailInput' className='w-full text-center text-base'><MD langCode={langCode}>{i18n.label_email_LB}</MD></label>
                      <input
                        id='emailInput'
                        className={cn('w-full rounded-lg border p-4 text-base', isEmailValid() ? 'border-gray-300' : 'border-red-600/40')}
                        placeholder={i18n.placeholder_email_LB}
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                      ></input>
                    </FlexCol>
                    <Reveal duration={250} delay={350} scale={0}>
                      <Button className={cn('p-8 text-lg disabled:opacity-40')} type='submit' disabled={!isFormValid() || sending}
                        id='SEND_MESSAGE_BUTTON'>
                        {sending ? i18n.sendingMessage : i18n.action_LB}
                      </Button>
                    </Reveal>
                    <div className='text-sm text-red-600'>{errorMsg}</div>
                  </form>
                </Section>}
            </>
          }
        </FlexCol>
      </div>
    </div>
  )
}
